import { deleteList as deleteListApi, getLists } from '@/apis/lists';
import { CatCampaignRecord } from '@/types';
import {
  AgDataGrid,
  AgDialog,
  domainCodeAtom,
  PaginationModel,
  useSaladBar,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { DialogContentText } from '@mui/material';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { SimpleCompleteListDialog } from '@pages/ListDetailPage/components/SimpleCompleteListDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  assigneesFilterOperators,
  coordinatorsFilterOperators,
  withNonEmptyFilterOperators,
} from '@/util/gridFilterOperators';
import { useMemo, useState } from 'react';
import {
  ALL_LISTS_TABLE_INITIAL_STATE,
  DEFAULT_TABLE_FILTER,
} from './listsPageTableState';
import EditListDialog from './EditListDialog';
import {
  createActionsColumn,
  getListTableColumnDefinitions,
  ListTableColumnName,
} from './ListsColumnDefinitions';

const NO_SAVED_LISTS = 'You have no saved lists.';
const NO_SAVED_LISTS_FILTER = 'You have no saved lists shown by the filter.';

const useCoordinatorLists = ({ isPrincipalCoordinator }: { isPrincipalCoordinator: boolean }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);

  const userSub = user?.sub ?? '';
  return useQuery({
    queryKey: [
      'lists',
      userSub,
      domainCode,
      'coordinator',
      isPrincipalCoordinator ? 'principal' : undefined,
    ],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getLists(token, domainCode, '', '', isPrincipalCoordinator ? '' : userSub);
    },
  });
};

const useDeleteList = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { enqueueSuccessNotification } = useSaladBar();

  return useMutation({
    mutationFn: async (listId: number) => {
      const token = await getAccessTokenSilently();
      return deleteListApi(token, listId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      enqueueSuccessNotification('List deleted');
    },
  });
};

export const CoordinatorTable = ({
  isPrincipalCoordinator = false,
}: {
  isPrincipalCoordinator?: boolean;
}) => {
  const [columnVisibilityModelSavedLists, setColumnVisibilityModelSavedLists] =
    useState<GridColumnVisibilityModel>({
      id: false,
    });
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: isPrincipalCoordinator ? 10 : 5,
  });

  const [listName, setListName] = useState('');
  const [campaign, setCampaign] = useState<CatCampaignRecord>();
  const [listId, setListId] = useState<number>();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const { data: lists, isLoading } = useCoordinatorLists({ isPrincipalCoordinator });

  const { mutate: deleteList } = useDeleteList();

  const handleOpenActionMenu = (id: string, name: string, theCampaign?: CatCampaignRecord) => {
    setListId(Number(id));
    setListName(name);
    setCampaign(theCampaign);
  };

  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleCompleteDialogOpen = () => {
    setCompleteDialogOpen(true);
  };

  const handleCompleteDialogClose = () => {
    setCompleteDialogOpen(false);
  };

  const handleEditDialogOpen = () => {
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleDeleteDialogDelete = async () => {
    deleteList(Number(listId));
    setDeleteDialogOpen(false);
  };

  const savedListsTableColumns = useMemo(() => {
    const columnNames: ListTableColumnName[] = isPrincipalCoordinator
      ? [
          'List Name',
          'Contacts',
          'Date Created',
          'Assignees',
          'Coordinators',
          'Campaign',
          'Status',
          'id',
        ]
      : ['List Name', 'Contacts', 'Date Created', 'Assignees', 'Campaign', 'Status', 'id'];

    const columns = getListTableColumnDefinitions(columnNames, { toDashboard: true });

    columns.push(
      createActionsColumn({
        handleDelete: handleDeleteDialogOpen,
        handleEdit: handleEditDialogOpen,
        handleOpen: handleOpenActionMenu,
        handleComplete: handleCompleteDialogOpen,
      })
    );
    return columns.map((column) => {
      if (column.field === 'contacts') {
        return column;
      }
      const next = withNonEmptyFilterOperators(column);
      if (column.field === 'assignees') {
        return { ...next, filterOperators: assigneesFilterOperators };
      }
      if (column.field === 'coordinators') {
        return { ...next, filterOperators: coordinatorsFilterOperators };
      }
      return next;
    });
  }, [isPrincipalCoordinator]);

  const skeletonRowWidths = savedListsTableColumns
    .map((column) =>
      columnVisibilityModelSavedLists && columnVisibilityModelSavedLists[column.field] === false
        ? 0
        : (column.flex ?? 0)
    )
    .filter((flex) => flex !== 0);
  return (
    <>
      <AgDataGrid
        loading={isLoading}
        rows={lists ?? []}
        columns={savedListsTableColumns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5]}
        noRowsMessage={NO_SAVED_LISTS}
        noResultsMessage={NO_SAVED_LISTS_FILTER}
        skeletonColumnWidths={skeletonRowWidths}
        columnVisibilityModel={columnVisibilityModelSavedLists}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModelSavedLists(newModel)}
        initialState={
          isPrincipalCoordinator ? ALL_LISTS_TABLE_INITIAL_STATE : DEFAULT_TABLE_FILTER
        }
        autoTableHeight={false}
      />
      <AgDialog
        dialogTitle={'Delete this list?'}
        isOpen={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        primaryButton={{
          text: 'Delete',
          onClick: handleDeleteDialogDelete,
        }}
      >
        <DialogContentText id="delete-confirm-dialog-description">
          Are you sure you want to delete the list <strong>{listName}</strong>?
        </DialogContentText>
      </AgDialog>
      <SimpleCompleteListDialog
        listId={listId || 0}
        listName={listName}
        isOpen={completeDialogOpen}
        onClose={handleCompleteDialogClose}
      />

      {listId && (
        <EditListDialog
          key={String(editDialogOpen)}
          isOpen={editDialogOpen}
          onClose={handleEditDialogClose}
          listName={listName}
          campaign={campaign}
          listId={listId}
        />
      )}
    </>
  );
};
